import type { BoxProps } from '@mui/material/Box';
import Box from '@mui/material/Box';
import { alpha as hexAlpha } from '@mui/material/styles';

import type { SettingsState } from '../types';
import { OptionButton } from './styles';

export type PresetsOptionsProps = BoxProps & {
    icon: React.ReactNode;
    value: SettingsState['primaryColor'];
    options: { name: SettingsState['primaryColor']; value: string }[];
    onChangeOption: (newOption: SettingsState['primaryColor']) => void;
};

/**
 * PresetsOptions component for displaying a grid of customizable option buttons.
 *
 * @component
 * @param {object} props - The properties for the PresetsOptions component
 * @param {SxProps} [props.sx] - The sx prop object or array for custom styling
 * @param {ReactNode} props.icon - The icon to display inside each option button
 * @param {string} props.value - The current selected option name
 * @param {Array<{name: string, value: string}>} props.options - Array of options to render as buttons
 * @param {Function} props.onChangeOption - Callback function when an option is selected, receives option name as parameter
 *
 * @returns {JSX.Element} A grid of option buttons that can be selected
 */
export function PresetsOptions({
    sx,
    icon,
    value,
    options,
    onChangeOption,
    ...other
}: PresetsOptionsProps) {
    return (
        <Box
            sx={[
                {
                    gap: 1.5,
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, 1fr)',
                },
                ...(Array.isArray(sx) ? sx : [sx]),
            ]}
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...other}
        >
            {options.map((option) => {
                const selected = value === option.name;

                return (
                    <OptionButton
                        key={option.name}
                        onClick={() => onChangeOption(option.name)}
                        sx={{
                            height: 64,
                            color: option.value,
                            ...(selected && {
                                bgcolor: hexAlpha(option.value, 0.08),
                            }),
                        }}
                    >
                        {icon}
                    </OptionButton>
                );
            })}
        </Box>
    );
}
